import React, { ErrorInfo, ReactNode } from 'react';
import {
    Avatar,
    Button,
    Flex,
    notification,
    Typography,
} from 'antd';
import { IoWarningOutline, IoReloadOutline, IoCopyOutline } from 'react-icons/io5';
import { addDatadogError } from '../../plugins/datadog';
import IMAGES from '../../const/images';

const { Title, Text } = Typography;

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    errorMessage: string;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, errorMessage: '' };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, errorMessage: error.message || 'Error desconocido' };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        addDatadogError(error, { componentStack: info.componentStack ?? '' });
    }

    handleCopy = () => {
        const { errorMessage } = this.state;
        navigator.clipboard.writeText(errorMessage || 'Error desconocido');
        notification.success({ message: 'Error copiado al portapapeles' });
    };

    render() {
        const { hasError, errorMessage } = this.state;
        const { children } = this.props;

        if (hasError) {
            return (
                <div
                    style={{
                        backgroundImage: `url(${IMAGES.login.spirals})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center center',
                        backgroundSize: 'cover',
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div
                        style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: 16,
                            padding: '48px 40px',
                            maxWidth: 420,
                            width: '100%',
                            textAlign: 'center',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <Avatar
                            size={64}
                            icon={<IoWarningOutline size={32} color="var(--color-purple)" />}
                            style={{
                                backgroundColor: 'var(--color-purple-2)',
                                marginBottom: 12,
                            }}
                        />
                        <Title level={4} style={{ marginBottom: 8 }}>
                            Algo salió mal
                        </Title>
                        <Text style={{ color: 'var(--color-dark-2)', display: 'block', marginBottom: 16 }}>
                            Ocurrió un error inesperado. Por favor, recarga la página.
                        </Text>
                        <Flex align="center" justify="center" gap={6} style={{ marginBottom: 24 }}>
                            <Text
                                code
                                style={{
                                    fontSize: 11,
                                    color: 'var(--color-dark-1)',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {errorMessage}
                            </Text>
                            <IoCopyOutline
                                size={14}
                                color="var(--color-dark-2)"
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                onClick={this.handleCopy}
                            />
                        </Flex>
                        <Button
                            icon={<IoReloadOutline />}
                            onClick={() => window.location.reload()}
                            size="large"
                            block
                            style={{
                                backgroundColor: 'var(--color-purple)',
                                borderColor: 'var(--color-purple)',
                                color: '#fff',
                            }}
                        >
                            Recargar página
                        </Button>
                    </div>
                </div>
            );
        }

        return children;
    }
}

export default ErrorBoundary;
